/**
 * Session resume helpers.
 *
 * When a user is auto-logged-out for inactivity, we stash the route they were on
 * so that after they log back in we can offer to take them right back there.
 * The entry is scoped to the user id (shared machines) and expires after a day
 * so a stale location is never silently restored.
 */
import type { User } from '../types/auth';

const RESUME_KEY = 'nivoon_resume_session';
const RESUME_TTL_MS = 24 * 60 * 60 * 1000; // 24h — aligns with the JWT lifetime

interface ResumeEntry {
  path: string;
  userId: string;
  ts: number;
}

// Routes we never want to resume into (public / auth / lockout pages).
const NON_RESUMABLE = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-email', '/trial-expired', '/email-verification-required'];

function isResumablePath(path: string): boolean {
  if (!path || path === '/') return false;
  return !NON_RESUMABLE.some(p => path === p || path.startsWith(`${p}?`) || path.startsWith(`${p}/`));
}

/** Persist the current location so it can be offered on next login. */
export function saveResumeLocation(userId: string | undefined): void {
  if (!userId) return;
  const path = window.location.pathname + window.location.search + window.location.hash;
  if (!isResumablePath(path)) return;
  try {
    const entry: ResumeEntry = { path, userId, ts: Date.now() };
    localStorage.setItem(RESUME_KEY, JSON.stringify(entry));
  } catch {
    /* localStorage unavailable — resume is best-effort */
  }
}

/**
 * Return the saved path if there is a fresh entry belonging to this user,
 * otherwise null. Does not clear the entry (call clearResumeLocation for that).
 */
export function getResumeLocation(userId: string | undefined): string | null {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(RESUME_KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw) as ResumeEntry;
    if (entry.userId !== userId) return null;
    if (Date.now() - entry.ts > RESUME_TTL_MS) { clearResumeLocation(); return null; }
    if (!isResumablePath(entry.path)) { clearResumeLocation(); return null; }
    return entry.path;
  } catch {
    return null;
  }
}

export function clearResumeLocation(): void {
  try { localStorage.removeItem(RESUME_KEY); } catch { /* ignore */ }
}

/** The default landing route for a user's role — mirrors Login.tsx post-login routing. */
export function roleDefaultPath(user: Pick<User, 'role' | 'customer_id'> | null | undefined): string {
  if (!user) return '/dashboard';
  if (user.role === 'customer' && user.customer_id) return `/customers/${user.customer_id}`;
  if (user.role === 'taskboard_user') return '/taskboard';
  if (user.role === 'professional_admin' || user.role === 'sales_user' || user.role === 'account_manager') return '/pro';
  // Consulting roles land in the Consulting app (Nivoon-Consulting.md §3): consultants
  // on their timesheet, managers on the dashboard.
  if (user.role === 'consultant') return '/time-billing';
  if (user.role === 'consulting_manager' || user.role === 'consulting_admin') return '/consulting';
  if (user.role === 'lite_admin' || user.role === 'lite_member') return '/lite';
  return '/dashboard';
}
